import {Switch} from '@headlessui/react';
import {curveLinear, curveStepAfter} from 'd3';
import {uniq} from 'lodash';
import React from 'react';
import {useQueryParam} from 'use-query-params';

import {useSelectedForecastTimes} from 'src/hooks/useSelectedForecastTimes';
import {useThresholdLinesForSite} from 'src/hooks/useThresholdLinesForSite';
import {
  ApiFlowContributingAreas,
  ForecastTimeSeries,
  ObservationTimeSeries,
  Project,
  SeriesType,
  Site,
} from 'src/lib/api/project';
import {
  AggregationMethod,
  AllColumns,
  ApiForecastTimeSeries,
  CBRFCColumns,
  CNRFCColumns,
  CompoundRFCColumns,
  DashboardSection,
  FLOW_OBSERVATION_SOURCES,
  FlowForecastColumns,
  FlowHistoricalDailyColumns,
  FlowObservationColumns,
  ForecastTime,
  NWRFCColumns,
  RFCColumns,
  RateVolumeMode,
  Source,
  TimeAggregation,
  WeatherColumns,
  isCumulativeMode,
} from 'src/lib/api/series';
import COLORS, {getRFCColors} from 'src/lib/colors';
import {addDays, addSeconds, makeDateRangeQueryParam, now} from 'src/lib/dates';
import featureFlags from 'src/lib/featureFlags';
import {
  getDefaultRateVolumeMode,
  getHasHFShortTermMean,
  hasShortTermFlow,
  hasShortTermWeather,
  isInvalidLevelMode,
  makeValidRateVolumeMode,
} from 'src/lib/siteUtils';
import {ConfigBySourceId, WEATHER_SOURCES} from 'src/lib/sourceConfigs';
import * as tw from 'src/lib/tailwindClasses';

import AggOptions, {AggState, TIME_AGGREGATIONS, checkTimeAggregationEnabled} from './AggOptions';
import ForecastFlowTextSummary, {SmartSummaryButton} from './ForecastFlowTextSummary';
import ForecastPrecipitationTextSummary from './ForecastPrecipitationTextSummary';
import ForecastSelect, {getForecastTimesForDays} from './ForecastSelect';
import {FreezingLine} from './FreezingLine';
import IncludeFlowContributingAreasSwitch from './IncludeContributingBasinsSwitch';
import {ThresholdLabelOverlays, ThresholdLines} from './ThresholdLines';
import TimeSeriesProvider from './TimeSeriesProvider';
import TimeSeriesViz, {
  SOLO_VIZ_HEIGHT,
  SOLO_VIZ_HEIGHT_WITH_LEGEND,
  STACKED_VIZ_HEIGHT,
  STACKED_VIZ_HEIGHT_WITH_LEGEND,
} from './TimeSeriesViz';
import {Translator, useTranslations} from './TranslationsContext';
import Viz from './Viz';
import {
  VizConfig,
  VizConfigCreatorItem,
  filterConfigToAvailableTimeSeries,
  hfGetStyle,
  makeConfigItems,
  makeLevelPercentileConfigs,
  makeRequestFromVizConfig,
  makeWeatherForecastItems,
  withDynamicHFCIBands,
} from './Viz/config';
import WithWidth from './WithWidth';

const HORIZON_DAYS = 10;
const DEFAULT_TIME_AGGREGATION_KEY = 'hourly';

// WeatherNext is too slow to fetch for all contributing areas at once.
const WEATHER_SOURCES_EXCLUDED_FOR_CONTRIBUTING_BASINS = [Source.WEATHERNEXT_2];

function getCommonWeatherSourcesForContributingBasins(
  mainSite: Site,
  project: Project,
  flowContributingAreas: ApiFlowContributingAreas | null | undefined
): Source[] {
  const allowedWeatherSources = new Set(
    WEATHER_SOURCES.filter(
      (source) => !WEATHER_SOURCES_EXCLUDED_FOR_CONTRIBUTING_BASINS.includes(source)
    )
  );

  const contributingBasinSiteIds = flowContributingAreas?.[mainSite.id]?.map((cb) => cb.siteId);

  if (!project.sites || !flowContributingAreas || !contributingBasinSiteIds?.length) {
    // If we don't have the necessary data, return all allowed weather sources
    return Array.from(allowedWeatherSources);
  }

  // Get all contributing basin sites
  const contributingBasinSites = contributingBasinSiteIds
    .map((siteId) => project.sites?.find((s) => s.id === siteId))
    .filter((site): site is Site => site !== undefined);

  // Helper to get weather sources from a site
  const getSources = (forecastTimeSeries: ForecastTimeSeries): Source[] =>
    forecastTimeSeries[SeriesType.WeatherShortTerm]
      .filter((ts) => allowedWeatherSources.has(ts.source as Source))
      .map((ts) => ts.source);

  // Start with main site's sources
  let commonSources = getSources(mainSite.forecastTimeSeries);

  // Filter to only sources that exist in all contributing basins
  for (const site of contributingBasinSites) {
    const siteSources = new Set(getSources(site.forecastTimeSeries));
    commonSources = commonSources.filter((source) => siteSources.has(source));
  }

  return commonSources;
}

/**
 * Refines the HF config to the columns the forecast response actually returned, once
 * the data is available. Memoized so it doesn't recompute (and produce a new config
 * identity, busting downstream Viz memos) on every parent re-render.
 */
function DynamicHFConfig({
  config,
  forecastTimeSeriesList,
  rateVolumeMode,
  timeAggregation,
  aggregationMethod,
  children,
}: {
  config: VizConfig;
  forecastTimeSeriesList: ApiForecastTimeSeries[];
  rateVolumeMode: RateVolumeMode;
  timeAggregation: TimeAggregation;
  aggregationMethod: AggregationMethod;
  children: (config: VizConfig) => JSX.Element;
}): JSX.Element {
  const displayConfig = React.useMemo(
    () =>
      // Level mode renders its own percentile lines, so leave it untouched.
      rateVolumeMode === 'level'
        ? config
        : withDynamicHFCIBands(config, forecastTimeSeriesList, {
            timeAggregation,
            aggregationMethod,
            rateVolumeMode,
          }),
    [config, forecastTimeSeriesList, rateVolumeMode, timeAggregation, aggregationMethod]
  );
  return children(displayConfig);
}

export default function ShortTerm({
  site,
  defaultDateRange: defaultDateRangeProp,
  includeFlowContributingAreas,
  setIncludeFlowContributingAreas,
  project,
  flowContributingAreas,
  currentUserId,
}: {
  site: Site;
  defaultDateRange?: [Date, Date];
  includeFlowContributingAreas: boolean;
  setIncludeFlowContributingAreas: (value: boolean) => void;
  project: Project;
  flowContributingAreas: ApiFlowContributingAreas | null | undefined;
  currentUserId?: string;
}) {
  const {t} = useTranslations();
  const [showSummary, setShowSummary] = React.useState(false);

  const hasObs = React.useMemo(
    () =>
      site.observationTimeSeries[SeriesType.FlowShortTerm].some((ts) =>
        FLOW_OBSERVATION_SOURCES.includes(ts.source)
      ),
    [site.observationTimeSeries]
  );

  const defaultDateRange = React.useMemo(
    () =>
      defaultDateRangeProp ||
      ([
        now(site.localTimeZone)
          .minus({
            days: hasObs ? HORIZON_DAYS : 1,
          })
          .startOf('day')
          .toJSDate(),
        now(site.localTimeZone).plus({days: HORIZON_DAYS}).endOf('day').toJSDate(),
      ] as [Date, Date]),
    [site.localTimeZone, hasObs, defaultDateRangeProp]
  );

  const DateRangeQueryParam = React.useMemo(
    () => makeDateRangeQueryParam(defaultDateRange),
    [defaultDateRange]
  );

  const primarySource = site.seriesTypes.includes(SeriesType.FlowShortTerm)
    ? Source.HydroForecastShortTerm
    : Source.ECMWFHRES;

  const [dateRange, setDateRange] = useQueryParam(`stDateRange`, DateRangeQueryParam);

  const [hfAgg, updateHfAgg] = React.useReducer(
    (state: AggState, action: Partial<AggState>) => {
      return {...state, ...action};
    },
    {
      timeAggregation: TIME_AGGREGATIONS[DEFAULT_TIME_AGGREGATION_KEY],
      aggregationMethod: 'mean',
      rateVolumeMode: getDefaultRateVolumeMode(site),
    }
  );

  React.useEffect(() => {
    updateHfAgg({rateVolumeMode: getDefaultRateVolumeMode(site)});
  }, [site]);

  const [weatherAgg, updateWeatherAgg] = React.useReducer(
    (state: AggState, action: Partial<AggState>) => {
      return {...state, ...action};
    },
    {
      timeAggregation: TIME_AGGREGATIONS[DEFAULT_TIME_AGGREGATION_KEY],
      aggregationMethod: 'mean',
      rateVolumeMode: 'rate',
    }
  );
  React.useEffect(() => {
    if (isInvalidLevelMode(weatherAgg.rateVolumeMode, site)) {
      updateWeatherAgg({rateVolumeMode: 'rate'});
    }
  }, [weatherAgg.rateVolumeMode, site, updateWeatherAgg]);

  // Calculate which weather sources are available across all contributing basins to prevent timeseries requests
  // from failing when some contributing basins don't have a common weather source.
  const weatherSources = React.useMemo(
    () =>
      includeFlowContributingAreas
        ? getCommonWeatherSourcesForContributingBasins(site, project, flowContributingAreas)
        : WEATHER_SOURCES,
    [includeFlowContributingAreas, site, project, flowContributingAreas]
  );

  // hfAgg and weatherAgg are eventually validated in the useEffect,
  // but we need to ensure they are valid on initial render to avoid noisy api errors
  const hfRateVolumeMode = makeValidRateVolumeMode(hfAgg.rateVolumeMode, site);
  const weatherRateVolumeMode = makeValidRateVolumeMode(weatherAgg.rateVolumeMode, site);

  const thresholdConfigs = useThresholdLinesForSite(site.id, currentUserId, hfRateVolumeMode);
  const [thresholdLinesVisible, setThresholdLinesVisible] = React.useState(true);

  const hfConfig = React.useMemo(
    () =>
      filterConfigToAvailableTimeSeries(
        makeHFConfig(
          site.observationTimeSeries,
          site.forecastTimeSeries,
          hfAgg.timeAggregation,
          hfAgg.aggregationMethod,
          hfRateVolumeMode,
          t
        ),
        site
      ),
    [site, hfAgg.timeAggregation, hfAgg.aggregationMethod, hfRateVolumeMode, t]
  );
  const weatherConfig = filterConfigToAvailableTimeSeries(
    makeWeatherConfig(
      site,
      weatherAgg.timeAggregation,
      weatherAgg.aggregationMethod,
      weatherRateVolumeMode,
      includeFlowContributingAreas,
      t,
      weatherSources
    ),
    site
  );

  const forecastSources = uniq(
    [...hfConfig.forecasts, ...weatherConfig.forecasts].map((c) => c.source)
  );

  React.useEffect(() => {
    if (!checkTimeAggregationEnabled(hfAgg.timeAggregation, dateRange[0], dateRange[1])) {
      if (hfAgg.timeAggregation === '1H') {
        updateHfAgg({timeAggregation: '1D'});
      }
      if (weatherAgg.timeAggregation === '1H') {
        updateWeatherAgg({timeAggregation: '1D'});
      }
    }
  }, [dateRange, hfAgg, updateHfAgg, weatherAgg, updateWeatherAgg]);

  const adjustedDateRange = [dateRange[0], addSeconds(dateRange[1], 1)] as [Date, Date];

  const {
    primaryForecastTimes,
    selectedPrimaryForecastTimes,
    selectedForecastTimesBySource,
    setSelectedPrimaryForecastTimes,
    onNextPrimaryForecastTimesChange,
    forecastTimesAreLoading,
  } = useSelectedForecastTimes(
    site,
    dateRange,
    HORIZON_DAYS,
    primarySource,
    forecastSources,
    true,
    false
  );

  // TODO, refine this.
  const shortTermSeries = site.forecastTimeSeries[SeriesType.FlowShortTerm].find(
    (series) => series.source === Source.HydroForecastShortTerm
  );
  const isUsingST3 =
    shortTermSeries?.sourceMetadata &&
    shortTermSeries.sourceMetadata.modelGeneration === 'short-term-3';

  return (
    <div className={tw.section}>
      <div className={tw.sectionHeader}>
        <div className={tw.withModelAnnotation}>
          <h2 className={tw.sectionTitle}>{t(DashboardSection.ShortTerm)}</h2>
          {featureFlags.ST3_VERSION_TAG && isUsingST3 && (
            <div className="px-2 py-1 rounded bg-blue-50">ST-3</div>
          )}
          {!showSummary && (
            <SmartSummaryButton siteId={site.id} onClick={() => setShowSummary(true)} />
          )}
        </div>
        <div className="flex items-center space-x-4">
          <ForecastSelect
            horizon={HORIZON_DAYS}
            site={site}
            dateRange={dateRange}
            defaultDateRange={defaultDateRange}
            setDateRange={setDateRange}
            primaryForecastTimes={primaryForecastTimes}
            selectedPrimaryForecastTimes={selectedPrimaryForecastTimes}
            setSelectedPrimaryForecastTimes={setSelectedPrimaryForecastTimes}
            onNextPrimaryForecastTimesChange={onNextPrimaryForecastTimesChange}
            shortcuts={{
              absolute: [
                {
                  displayName: t('latest'),
                  getSelection: (forecastTimes: ForecastTime[]) => [forecastTimes[0]],
                },
                {
                  displayName: t('last3Days'),
                  getSelection: (forecastTimes: ForecastTime[]) =>
                    getForecastTimesForDays(forecastTimes, 3),
                  startDate: hasObs ? defaultDateRange[0] : addDays(defaultDateRange[0], -3),
                  endDate: defaultDateRange[1],
                },
                {
                  displayName: t('last10Days'),
                  getSelection: (forecastTimes: ForecastTime[]) =>
                    getForecastTimesForDays(forecastTimes, 10),
                  startDate: hasObs ? defaultDateRange[0] : addDays(defaultDateRange[0], -10),
                  endDate: defaultDateRange[1],
                },
              ],
            }}
          />
        </div>
      </div>
      {showSummary && (
        <div className="my-4 p-4 bg-blue-50 rounded-lg">
          <ForecastFlowTextSummary
            site={site}
            source={Source.HydroForecastShortTerm}
            horizonDays={HORIZON_DAYS}
            section={DashboardSection.ShortTerm}
          />
          <ForecastPrecipitationTextSummary
            site={site}
            horizonDays={HORIZON_DAYS}
            section={DashboardSection.ShortTerm}
          />
        </div>
      )}
      <WithWidth>
        {(width) => (
          <div className={tw.sectionBody}>
            {hasShortTermFlow(site) && (
              <TimeSeriesProvider
                site={site}
                section={DashboardSection.ShortTerm}
                forecastTimesBySource={selectedForecastTimesBySource}
                dateRange={adjustedDateRange}
                isStaging={false}
                forecastTimesAreLoading={forecastTimesAreLoading}
                {...makeRequestFromVizConfig(hfConfig)}
              >
                {(vizProps) => (
                  // The request uses the full hfConfig (superset of columns); here,
                  // post-fetch, we refine the HF CI bands to what the response actually
                  // returned (memoized inside DynamicHFConfig).
                  <DynamicHFConfig
                    config={hfConfig}
                    forecastTimeSeriesList={vizProps.forecastTimeSeriesList}
                    rateVolumeMode={hfRateVolumeMode}
                    timeAggregation={hfAgg.timeAggregation}
                    aggregationMethod={hfAgg.aggregationMethod}
                  >
                    {(displayConfig) => (
                      <Viz
                        section={t(DashboardSection.ShortTerm)}
                        title={hfRateVolumeMode === 'level' ? t('waterLevel') : t('flow')}
                        renderOptions={() => (
                          <AggOptions
                            site={site}
                            agg={{...hfAgg, rateVolumeMode: hfRateVolumeMode}}
                            updateAgg={updateHfAgg}
                            dateRange={dateRange}
                          />
                        )}
                        site={site}
                        loadingStateDims={[width, SOLO_VIZ_HEIGHT_WITH_LEGEND]}
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        config={displayConfig}
                        {...vizProps}
                        thresholdLinesVisible={thresholdLinesVisible}
                        onToggleThresholdLines={
                          thresholdConfigs.length > 0
                            ? () => setThresholdLinesVisible((v) => !v)
                            : undefined
                        }
                      >
                        {(props) => (
                          <TimeSeriesViz
                            {...props}
                            timeZone={site.localTimeZone}
                            dims={[width, SOLO_VIZ_HEIGHT]}
                            renderSvg={
                              thresholdLinesVisible && thresholdConfigs.length > 0
                                ? ({y, graphDims}) => (
                                    <ThresholdLines
                                      y={y}
                                      graphDims={graphDims}
                                      thresholdConfigs={thresholdConfigs}
                                    />
                                  )
                                : undefined
                            }
                            renderHtmlOverlay={
                              thresholdLinesVisible && thresholdConfigs.length > 0
                                ? ({y, graphDims, margin}) => (
                                    <ThresholdLabelOverlays
                                      y={y}
                                      graphDims={graphDims}
                                      margin={margin}
                                      thresholdConfigs={thresholdConfigs}
                                    />
                                  )
                                : undefined
                            }
                          />
                        )}
                      </Viz>
                    )}
                  </DynamicHFConfig>
                )}
              </TimeSeriesProvider>
            )}
            {hasShortTermWeather(site) && (
              <TimeSeriesProvider
                site={site}
                section={DashboardSection.ShortTerm}
                dateRange={adjustedDateRange}
                forecastTimesBySource={selectedForecastTimesBySource}
                forecastTimesAreLoading={forecastTimesAreLoading}
                {...makeRequestFromVizConfig(weatherConfig)}
              >
                {(vizProps) => {
                  return (
                    <Viz
                      section={t(DashboardSection.ShortTerm)}
                      title={t('weather')}
                      renderOptions={() => (
                        <>
                          <IncludeFlowContributingAreasSwitch
                            site={site}
                            basinAveragingEnabled={project.basinAveragingEnabled}
                            includeFlowContributingAreas={includeFlowContributingAreas}
                            setIncludeFlowContributingAreas={setIncludeFlowContributingAreas}
                          />
                          <div className="w-1" />
                          <AggOptions
                            site={site}
                            agg={{...weatherAgg, rateVolumeMode: weatherRateVolumeMode}}
                            updateAgg={updateWeatherAgg}
                            dateRange={dateRange}
                            showLevelOption={false}
                          />
                        </>
                      )}
                      site={site}
                      loadingStateDims={[width, STACKED_VIZ_HEIGHT_WITH_LEGEND * 2]}
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                      config={weatherConfig}
                      {...vizProps}
                    >
                      {(props) => (
                        <>
                          <TimeSeriesViz
                            {...props}
                            timeZone={site.localTimeZone}
                            groupId={t('precipitation')}
                            dims={[width, STACKED_VIZ_HEIGHT]}
                            range={[0, 'auto']}
                            minMaxRangeConstraints={[null, 0.1]}
                          />
                          <TimeSeriesViz
                            {...props}
                            timeZone={site.localTimeZone}
                            groupId={t('temperature')}
                            dims={[width, STACKED_VIZ_HEIGHT]}
                            renderSvg={({y, graphDims, observationTimeSeriesList}) => (
                              <FreezingLine
                                y={y}
                                graphDims={graphDims}
                                temperatureTimeSeries={
                                  observationTimeSeriesList.find(
                                    (ts) => !!ts.variables[WeatherColumns.TEMPERATURE]
                                  )!
                                }
                              />
                            )}
                          />
                        </>
                      )}
                    </Viz>
                  );
                }}
              </TimeSeriesProvider>
            )}
          </div>
        )}
      </WithWidth>
    </div>
  );
}

function makeHFConfig(
  observationTimeSeries: ObservationTimeSeries,
  forecastTimeSeries: ForecastTimeSeries,
  timeAggregation: TimeAggregation,
  aggregationMethod: AggregationMethod,
  rateVolumeMode: RateVolumeMode,
  t: Translator
): VizConfig {
  const curveFactory = timeAggregation === '1H' ? curveLinear : curveStepAfter;

  const hasMean = getHasHFShortTermMean(forecastTimeSeries);

  const observations: VizConfigCreatorItem[] = [
    {
      displayName: t('historicalMean'),
      source: Source.FLOW_HISTORICAL_DAILY,
      columns: [FlowHistoricalDailyColumns.FLOW_MEAN],
      getStyle: () => ({stroke: COLORS.OBS, strokeDasharray: 2}),
      legendIcon: 'line',
      curveFactory,
      isHidden: isCumulativeMode(rateVolumeMode),
    },
    ...observationTimeSeries[SeriesType.FlowShortTerm]
      .filter((q) => FLOW_OBSERVATION_SOURCES.includes(q.source) && q.columns && q.columns.length)
      .flatMap((q) => {
        // trend is based on the presence of the observed_clean column, which
        // is added by the api at request-time. If this is present, we will add
        // an additional timeseries, and style the Observed timeseries
        // differently, and toggle its visibility off by default
        const hasTrend = observationTimeSeries[SeriesType.FlowShortTerm].some(
          (q) =>
            FLOW_OBSERVATION_SOURCES.includes(q.source) &&
            q.columns &&
            q.columns.includes(FlowObservationColumns.observed_clean)
        );

        // because "Observed" can come in so many flavors, we use anything with a
        // valid Observation Source as observed, and trust the column provided by
        // the API completely
        const cs: VizConfigCreatorItem[] = [
          {
            displayName: t('observed'),
            source: q.source,
            columns: [q.columns![0]] as AllColumns[],
            getStyle: () => ({
              hfStrokeOutlineStyle: {
                stroke: 'white',
                strokeWidth: 3,
              },
              stroke: COLORS.OBS,
              strokeOpacity: hasTrend ? 0.4 : 1,
              strokeWidth: 2,
              fill: rateVolumeMode === 'level' ? COLORS.OBS : undefined,
              fillOpacity: rateVolumeMode === 'level' ? 0.05 : undefined,
            }),
            legendIcon: 'line',
            curveFactory,
            isHidden: isCumulativeMode(rateVolumeMode),
            defaultNotVisible: hasTrend,
          },
        ];

        if (hasTrend) {
          cs.push({
            displayName: t('observedClean'),
            source: q.source,
            columns: [FlowObservationColumns.observed_clean],
            getStyle: () => ({
              hfStrokeOutlineStyle: {
                stroke: 'white',
                strokeWidth: 3,
              },
              stroke: COLORS.OBS,
              strokeOpacity: 1,
              strokeWidth: 2,
              fill: rateVolumeMode === 'level' ? COLORS.OBS : undefined,
              fillOpacity: rateVolumeMode === 'level' ? 0.05 : undefined,
            }),
            legendIcon: 'line',
            isHidden: isCumulativeMode(rateVolumeMode),
            curveFactory,
          });
        }

        return cs;
      }),
  ];

  const hasRFC = forecastTimeSeries[SeriesType.FlowShortTerm].some((q) => q.source === Source.RFC);
  const hasNwrfc = forecastTimeSeries[SeriesType.FlowShortTerm].some(
    (q) => q.source === Source.NWRFC
  );
  const hasCbrfc = forecastTimeSeries[SeriesType.FlowShortTerm].some(
    (q) => q.source === Source.CBRFC
  );
  const hasCnrfc = forecastTimeSeries[SeriesType.FlowShortTerm].some(
    (q) => q.source === Source.CNRFC
  );
  const hasCompoundRfc = forecastTimeSeries[SeriesType.FlowShortTerm].some(
    (q) => q.source === Source.COMPOUND_RFC
  );

  const forecasts: VizConfigCreatorItem[] = [
    {
      displayName: 'HydroForecast 80% CI',
      source: Source.HydroForecastShortTerm,
      columns: [FlowForecastColumns.DISCHARGE_0_10, FlowForecastColumns.DISCHARGE_0_90],
      getStyle: () => ({}),
      legendIcon: 'rect',
      hideIfMultipleForecastTimes: true,
      isHidden: true,
      curveFactory,
    },
    {
      displayName: `HydroForecast ${t('median')}`,
      source: Source.HydroForecastShortTerm,
      columns: [FlowForecastColumns.DISCHARGE_MEDIAN],
      getStyle: hfGetStyle,
      legendIcon: 'line',
      isHidden: hasMean,
      showForecastTimeMarkers: !hasMean,
      curveFactory,
    },
  ];
  if (rateVolumeMode === 'level') {
    forecasts.push(
      ...makeLevelPercentileConfigs({
        source: Source.HydroForecastShortTerm,
        curveFactory,
        baseColor: COLORS.HF_50,
        displayNamePrefix: 'HydroForecast',
      })
    );
  } else {
    forecasts.push({
      displayName: 'HydroForecast 50% CI',
      source: Source.HydroForecastShortTerm,
      columns: [FlowForecastColumns.DISCHARGE_0_25, FlowForecastColumns.DISCHARGE_0_75],
      getStyle: () => ({
        fill: COLORS.HF_50,
        fillOpacity: 0.6,
      }),
      legendIcon: 'rect',
      hideIfMultipleForecastTimes: true,
      curveFactory,
    });
    forecasts.push({
      displayName: 'HydroForecast 90% CI',
      source: Source.HydroForecastShortTerm,
      columns: [FlowForecastColumns.DISCHARGE_0_05, FlowForecastColumns.DISCHARGE_0_95],
      getStyle: () => ({
        fill: COLORS.HF_90,
        fillOpacity: 0.3,
      }),
      legendIcon: 'rect',
      hideIfMultipleForecastTimes: true,
      curveFactory,
    });
  }

  if (hasMean) {
    forecasts.push({
      displayName: `HydroForecast ${t('mean')}`,
      source: Source.HydroForecastShortTerm,
      columns: [FlowForecastColumns.DISCHARGE_MEAN],
      getStyle: hfGetStyle,
      legendIcon: 'line',
      showForecastTimeMarkers: true,
      curveFactory,
    });
  }

  if (hasRFC) {
    forecasts.push({
      displayName: 'NOAA RFC',
      source: Source.RFC,
      columns: [RFCColumns.FLOW],
      getStyle: (idx = 0, numForecasts = 0) => {
        let strokeColor;
        if (numForecasts === 0) {
          const rfcColors = getRFCColors(0);
          strokeColor = rfcColors[rfcColors.length - 1];
        } else {
          const rfcColors = getRFCColors(numForecasts);
          strokeColor = rfcColors[idx];
        }
        return {stroke: strokeColor, strokeDasharray: 5};
      },
      legendIcon: 'line',
      showForecastTimeMarkers: true,
      curveFactory,
    });
  }

  if (hasNwrfc) {
    const hasNwrfcDischarge = forecastTimeSeries[SeriesType.FlowShortTerm].some(
      (q) => q.columns && q.columns?.indexOf(NWRFCColumns.DISCHARGE) > -1
    );
    const hasNwrfcInflow = forecastTimeSeries[SeriesType.FlowShortTerm].some(
      (q) => q.columns && q.columns?.indexOf(NWRFCColumns.INFLOW) > -1
    );

    let nwrfcColumns = null;
    if (hasNwrfcInflow) {
      nwrfcColumns = [NWRFCColumns.INFLOW];
    } else if (hasNwrfcDischarge) {
      nwrfcColumns = [NWRFCColumns.DISCHARGE];
    }

    if (nwrfcColumns !== null) {
      forecasts.push({
        displayName: 'NOAA NWRFC',
        source: Source.NWRFC,
        columns: nwrfcColumns,
        getStyle: (idx = 0, numForecasts = 0) => {
          let strokeColor;
          if (numForecasts === 0) {
            const rfcColors = getRFCColors(0);
            strokeColor = rfcColors[rfcColors.length - 1];
          } else {
            const rfcColors = getRFCColors(numForecasts);
            strokeColor = rfcColors[idx];
          }
          return {stroke: strokeColor, strokeDasharray: 5};
        },
        legendIcon: 'line',
        showForecastTimeMarkers: true,
        curveFactory,
      });
    }
  }

  if (hasCbrfc) {
    forecasts.push({
      displayName: 'NOAA CBRFC',
      source: Source.CBRFC,
      columns: [CBRFCColumns.FLOW], // only ever one column for CBRFC
      getStyle: (idx = 0, numForecasts = 0) => {
        let strokeColor;
        if (numForecasts === 0) {
          const rfcColors = getRFCColors(0);
          strokeColor = rfcColors[rfcColors.length - 1];
        } else {
          const rfcColors = getRFCColors(numForecasts);
          strokeColor = rfcColors[idx];
        }
        return {stroke: strokeColor, strokeDasharray: 5};
      },
      legendIcon: 'line',
      showForecastTimeMarkers: true,
      curveFactory,
    });
  }

  if (hasCnrfc) {
    const hasCnrfcEnsemble = forecastTimeSeries[SeriesType.FlowShortTerm].some(
      (q) => q.columns && q.columns?.indexOf(CNRFCColumns.DISCHARGE_ENSEMBLE) > -1
    );
    const hasCnrfcDeterministic = forecastTimeSeries[SeriesType.FlowShortTerm].some(
      (q) => q.columns && q.columns?.indexOf(CNRFCColumns.DISCHARGE_DETERMINISTIC) > -1
    );

    let cnrfcColumns = null;
    if (hasCnrfcEnsemble) {
      cnrfcColumns = [CNRFCColumns.DISCHARGE_ENSEMBLE];
    } else if (hasCnrfcDeterministic) {
      cnrfcColumns = [CNRFCColumns.DISCHARGE_DETERMINISTIC];
    }

    if (cnrfcColumns !== null) {
      forecasts.push({
        displayName: 'NOAA CNRFC',
        source: Source.CNRFC,
        columns: cnrfcColumns,
        getStyle: (idx = 0, numForecasts = 0) => {
          let strokeColor;
          if (numForecasts === 0) {
            const rfcColors = getRFCColors(0);
            strokeColor = rfcColors[rfcColors.length - 1];
          } else {
            const rfcColors = getRFCColors(numForecasts);
            strokeColor = rfcColors[idx];
          }
          return {stroke: strokeColor, strokeDasharray: 5};
        },
        legendIcon: 'line',
        showForecastTimeMarkers: true,
        curveFactory,
      });
    }
  }

  if (hasCompoundRfc) {
    forecasts.push({
      displayName: 'NOAA RFC',
      source: Source.COMPOUND_RFC,
      columns: [CompoundRFCColumns.DISCHARGE],
      getStyle: (idx = 0, numForecasts = 0) => {
        let strokeColor;
        if (numForecasts === 0) {
          const rfcColors = getRFCColors(0);
          strokeColor = rfcColors[rfcColors.length - 1];
        } else {
          const rfcColors = getRFCColors(numForecasts);
          strokeColor = rfcColors[idx];
        }
        return {stroke: strokeColor, strokeDasharray: 5};
      },
      legendIcon: 'line',
      showForecastTimeMarkers: true,
      curveFactory,
    });
  }

  return {
    forecasts: makeConfigItems('forecasts', forecasts, {
      timeAggregation,
      aggregationMethod,
      rateVolumeMode,
    }),
    observations: makeConfigItems('observations', observations, {
      timeAggregation,
      aggregationMethod,
      rateVolumeMode,
    }),
  };
}

function makeWeatherConfig(
  site: Site,
  timeAggregation: TimeAggregation,
  aggregationMethod: AggregationMethod,
  rateVolumeMode: RateVolumeMode,
  includeFlowContributingAreas: boolean,
  t: Translator,
  weatherSources: Source[]
): VizConfig {
  const curveFactory = timeAggregation === '1H' ? curveLinear : curveStepAfter;

  const obsSource = site.observationTimeSeries[SeriesType.WeatherShortTerm].some(
    (ts) => ts.source === Source.HRRR
  )
    ? Source.HRRR
    : Source.ECMWFHRES;

  // Check if the observation source is available across contributing basins (e.g. HRRR is only available in some basins)
  const obsSourceAvailable = weatherSources.includes(obsSource);

  // Ensure consistent sorting for ease of reading legend and consistent colors
  const sortedWeatherSources = weatherSources.sort(
    (a, b) => WEATHER_SOURCES.indexOf(a as Source) - WEATHER_SOURCES.indexOf(b as Source)
  );

  return {
    forecasts: makeConfigItems(
      'forecasts',
      sortedWeatherSources.flatMap((source) => makeWeatherForecastItems(source, curveFactory, t)),
      {
        timeAggregation,
        aggregationMethod,
        rateVolumeMode,
        includeFlowContributingAreas,
      }
    ),
    observations: makeConfigItems(
      'observations',
      obsSourceAvailable
        ? [
            {
              displayName: `${ConfigBySourceId[obsSource].display} ${t('observed')}`,
              source: obsSource,
              columns: [WeatherColumns.PRECIPITATION],
              getStyle: () => ({
                stroke: COLORS.OBS,
                fill: COLORS.OBS,
                fillOpacity: 0.1,
                hfStrokeOutlineStyle: {
                  stroke: 'white',
                  strokeWidth: 3,
                },
              }),
              legendIcon: 'rect',
              groupId: t('precipitation'),
              isHidden: isCumulativeMode(rateVolumeMode),
              curveFactory,
            },
            {
              displayName: `${ConfigBySourceId[obsSource].display} ${t('observed')}`,
              source: obsSource,
              columns: [WeatherColumns.TEMPERATURE],
              getStyle: () => ({
                stroke: COLORS.OBS,
                hfStrokeOutlineStyle: {
                  stroke: 'white',
                  strokeWidth: 3,
                },
              }),
              legendIcon: 'line',
              groupId: t('temperature'),
              curveFactory,
            },
          ]
        : [],
      {
        timeAggregation,
        aggregationMethod,
        rateVolumeMode,
        includeFlowContributingAreas,
      }
    ),
  };
}
